import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';

import { useSignup } from '../context';
import type { Flash, Step } from '../flow';
import { tenantDashboardHost } from '../host';
import { CODE_TAKEN_PICK_WORDS, errorText } from '../labels';
import type { CodeOption, CreateBody, Created } from '../types';

// Create account (plan §8): the admin's name and password, then `POST {restech}/create` — the tenant
// and its admin in one transaction. Refusals go back to the screen that can fix them.

interface Props {
    onCreated: (created: Created) => void;
}

type FieldErrors = Partial<Record<'first_name' | 'last_name' | 'password' | 'confirm', string>>;

const words = (messages: unknown): string => (Array.isArray(messages) ? messages.join(' ') : String(messages));

/** Where a create refusal is fixed: [step, flash field] — null stays on this screen. Any other refusal of the
 *  booking system (422, the restech's own codes) goes back to Connect too (createRefusalRoute). */
export const CREATE_REFUSAL_ROUTES: Record<string, [Step, string?]> = {
    code_taken: ['confirm', 'code'],
    code_not_offered: ['confirm', 'code'],
    code_unavailable: ['confirm', 'code'],
    bad_candidate: ['confirm', 'logo'],
    already_onboarded: ['confirm'],
    email_not_verified: ['verify'],
    account_not_confirmed: ['connect'],
    credential_rejected: ['connect'],
    no_companies: ['connect'],
    account_not_found: ['connect'],
    restech_not_allowed: ['connect'],
    unknown_restech: ['connect'],
    not_connected: ['connect'],
};

/** The route of a create refusal, or null to stay. */
export function createRefusalRoute(error: any): [Step, string?] | null {
    if (!error || typeof error.code !== 'string') {
        return null;
    }
    if (Object.prototype.hasOwnProperty.call(CREATE_REFUSAL_ROUTES, error.code)) {
        return CREATE_REFUSAL_ROUTES[error.code];
    }
    return error.status === 422 && error.code !== 'invalid' ? ['connect'] : null;
}

// `invalid` field errors outside the admin's own fields belong to an earlier screen.
const INVALID_FIELD_ROUTES: Record<string, [Step, string?]> = {
    email: ['verify'],
    credentials: ['connect'],
    account: ['connect'],
    code: ['confirm', 'code'],
    name: ['confirm'],
    lang_enabled: ['confirm'],
    default_lang: ['confirm'],
    lang_fallback: ['confirm'],
    logo: ['confirm', 'logo'],
    has_app: ['confirm', 'has_app'],
    branchio_key: ['confirm', 'branchio_key'],
    branchio_secret: ['confirm', 'branchio_secret'],
    sandbox: ['confirm'],
};

export default function CreateAccount({ onCreated }: Props) {
    const { api, flow, dispatch, sessionLost } = useSignup();
    const invite = flow.preview!.invite;
    const holderEmail = flow.preview?.holder?.email || null;
    const signInEmail = invite.bound ? holderEmail || invite.email_masked : flow.verifiedEmail;
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [confirm, setConfirm] = React.useState('');
    const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
    const [error, setError] = React.useState<string | null>(flow.flash ? flow.flash.message : null);
    const [busy, setBusy] = React.useState(false);
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);

    const details = flow.details!;
    const view = flow.view!;
    // The confirm card's pick (or, from an older backend without code options, the derived code or the typed one).
    const code = details.code;
    // Sent when picked from code options or typed (code edits). An account view without options came from an older
    // backend: its read-only code is left to the server's default, as before (the deploy window, review L5).
    const sendCode = !!(view.derived.code_options && view.derived.code_options.length) || view.code_editable;

    const goBack = (
        step: Step, message: string, field?: string,
        extra: Partial<{ alreadyOnboarded: boolean; unverify: boolean; codeOptions: CodeOption[] }> = {},
    ) => {
        const flash: Flash = field ? { message, field } : { message };
        dispatch({ type: 'go', step, flash, ...extra });
    };

    const onSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (busy) { return; }
        const problems: FieldErrors = {};
        if (!firstName.trim()) { problems.first_name = 'Enter your first name.'; }
        if (!password) { problems.password = 'Choose a password.'; }
        if (password && confirm !== password) { problems.confirm = 'The passwords do not match.'; }
        setFieldErrors(problems);
        setError(null);
        if (Object.keys(problems).length) { return; }

        const fallback = view.derived.lang_fallback.filter((l) => details.lang_enabled.includes(l));
        const body: CreateBody = {
            credentials: flow.credentials,
            account: flow.account!,
            name: details.name,
            lang_enabled: details.lang_enabled,
            default_lang: details.default_lang,
            lang_fallback: fallback.length ? fallback : [details.default_lang],
            logo: details.logo,
            has_app: !!details.has_app,
            admin: { first_name: firstName.trim(), last_name: lastName.trim(), password },
        };
        if (details.has_app && details.branchio_key && details.branchio_secret) {
            body.branchio_key = details.branchio_key;
            body.branchio_secret = details.branchio_secret;
        }
        if (sendCode) {
            body.code = code;
        }
        if (!invite.bound && flow.verifiedEmail) {
            body.email = flow.verifiedEmail;
        }
        // Asked only while sandboxes are offered; an older backend (or one with them off) gets nothing.
        if (flow.sandboxOffered) {
            body.sandbox = !!details.sandbox;
        }

        setBusy(true);
        try {
            const created = await api.create(flow.restech!.code, body);
            onCreated(created);
            return;
        } catch (e: any) {
            if (sessionLost(e)) { return; }
            if (!mounted.current) { return; }
            // Only closed tenants hold the company: ops finish the setup with the client (an end screen, no retry).
            if (e && e.code === 'returning_client') {
                dispatch({ type: 'handed_over', message: errorText(e) });
                return;
            }
            // Taken meanwhile: the answer offers the code options afresh, to pick from on the confirm card.
            const codeOptions: CodeOption[] | null = (e && e.code === 'code_taken' && e.codeOptions) || null;
            const message = codeOptions ? CODE_TAKEN_PICK_WORDS : errorText(e);
            const route = createRefusalRoute(e);
            if (route) {
                goBack(route[0], message, route[1], {
                    alreadyOnboarded: e.code === 'already_onboarded',
                    unverify: e.code === 'email_not_verified',
                    codeOptions: codeOptions || undefined,
                });
                return;
            }
            if (e && e.code === 'invalid') {
                const errs = e.errors || {};
                const admin = errs.admin && typeof errs.admin === 'object' ? errs.admin : {};
                const mine: FieldErrors = {};
                (['first_name', 'last_name', 'password'] as const).forEach((k) => {
                    if (admin[k]) { mine[k] = words(admin[k]); }
                });
                const elsewhere = Object.keys(errs).find((k) => k !== 'admin' && INVALID_FIELD_ROUTES[k]);
                if (!Object.keys(mine).length && elsewhere) {
                    const [step, field] = INVALID_FIELD_ROUTES[elsewhere];
                    const detail = typeof errs[elsewhere] === 'object' && !Array.isArray(errs[elsewhere])
                        ? message : words(errs[elsewhere]);
                    goBack(step, detail, field, { unverify: elsewhere === 'email' });
                    return;
                }
                setFieldErrors(mine);
                if (!Object.keys(mine).length) { setError(message); }
            } else {
                setError(message);
            }
        }
        if (mounted.current) { setBusy(false); }
    };

    return (
        <Box component="form" noValidate onSubmit={onSubmit} aria-label="Create your account">
            <Typography variant="h5" component="h1" gutterBottom>Create your account</Typography>
            <Typography color="text.secondary" paragraph>
                Last step: you become the administrator of <strong>{details.name}</strong> on TourOptima, with your dashboard
                at <strong>{tenantDashboardHost(code)}</strong>.
            </Typography>
            {signInEmail && (
                <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">You will sign in with</Typography>
                    <Typography>
                        <strong>{signInEmail}</strong>
                        {!invite.bound && (
                            <>
                                {' '}
                                <Link component="button" type="button" variant="body2" disabled={busy}
                                    onClick={() => dispatch({ type: 'go', step: 'verify', unverify: true })}>
                                    Change
                                </Link>
                            </>
                        )}
                    </Typography>
                </Box>
            )}
            {/* For password managers: the account's user name. */}
            {signInEmail && !signInEmail.includes('•') && (
                <input type="email" name="username" autoComplete="username" value={signInEmail} readOnly hidden />
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                <TextField
                    id="signup-first-name"
                    label="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    margin="normal"
                    required
                    autoFocus
                    autoComplete="given-name"
                    inputProps={{ maxLength: 150 }}
                    error={!!fieldErrors.first_name}
                    helperText={fieldErrors.first_name || ' '}
                    disabled={busy}
                />
                <TextField
                    id="signup-last-name"
                    label="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    margin="normal"
                    autoComplete="family-name"
                    inputProps={{ maxLength: 150 }}
                    error={!!fieldErrors.last_name}
                    helperText={fieldErrors.last_name || ' '}
                    disabled={busy}
                />
            </Box>
            <TextField
                id="signup-password"
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoComplete="new-password"
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || 'Use a password you do not use anywhere else.'}
                disabled={busy}
            />
            <TextField
                id="signup-password-confirm"
                type="password"
                label="Repeat the password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                fullWidth
                margin="normal"
                required
                autoComplete="new-password"
                error={!!fieldErrors.confirm}
                helperText={fieldErrors.confirm || ' '}
                disabled={busy}
            />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <Button type="button" disabled={busy} onClick={() => dispatch({ type: 'go', step: 'confirm' })}>
                    Back
                </Button>
                <Button type="submit" variant="contained" disabled={busy}
                    startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}>
                    {busy ? 'Creating your account…' : 'Create account'}
                </Button>
            </Box>
        </Box>
    );
}
